import { lazyWithRetry, LazyRouteComponent } from "../Util/lazyWithRetry";
import { ALERT_LIST_PATH } from "../Pages/Alert/alertConstants";
import { MARKETPLACE_ROUTE } from "../Pages/Marketplace/marketplaceConstants";
import { OBJECTIVE_LIST_PATH } from "../Pages/Objective/objectiveRoutes";
import { PROJECT_LIST_PATH } from "../Pages/Project/projectConstants";

/**
 * All lazily loaded route components (route-based code splitting) plus a small
 * path → preload registry used to warm chunks ahead of navigation (sign-in landing
 * page, sidebar hover). Lives outside App.tsx so the sidebar can import it without
 * creating an import cycle through App.
 */

// ---------------------------------------------------------------------------
// Lazy routes (route-based code splitting, see lazyLoadingPlanVanAanpak.md).
// Each page below is fetched on first navigation instead of at startup; a stale
// chunk after a deploy triggers one automatic reload (lazyWithRetry).
// ---------------------------------------------------------------------------
export const Metric = lazyWithRetry(() => import("../Pages/System/Metric/Metric"));
export const AskAI = lazyWithRetry(() => import("../Pages/AskAI/AskAI"));
export const DashboardView = lazyWithRetry(() => import("../Pages/Dashboards/Dashboard/DashboardView"));
export const DashboardViewer = lazyWithRetry(() => import("../Pages/DashboardViewer/DashboardViewer"));
export const HomeScreen = lazyWithRetry(() => import("../Pages/Home/Home"));
export const Component = lazyWithRetry(() => import("../Pages/Management/Component/component"));
export const CustomerWizard = lazyWithRetry(() => import("../Pages/Management/Customer/Wizard/CustomerWizard"));
export const DashboardCategory = lazyWithRetry(() => import("../Pages/Management/DashboardCategory/DashboardCategory"));
export const Dataservice = lazyWithRetry(() => import("../Pages/Management/Dataservice/Dataservice"));
export const AuthenticationType = lazyWithRetry(() => import("../Pages/Management/AuthenticationType/AuthenticationType"));
export const Permission = lazyWithRetry(() => import("../Pages/Management/Permission/permission"));
export const AccessCenter = lazyWithRetry(() => import("../Pages/Management/Access/AccessCenter"));
export const RoleEditor = lazyWithRetry(() => import("../Pages/Management/Access/RoleEditor"));
export const UserEditor = lazyWithRetry(() => import("../Pages/Management/Access/UserEditor"));
export const Marketplace = lazyWithRetry(() => import("../Pages/Marketplace/Marketplace"));
export const MarketplaceDatasourceDetailPage = lazyWithRetry(() => import("../Pages/Marketplace/MarketplaceDatasourceDetailPage"));
export const MarketplaceDatasourceSetupPage = lazyWithRetry(() => import("../Pages/Marketplace/MarketplaceDatasourceSetupPage"));
export const Notifications = lazyWithRetry(() => import("../Pages/Notifications/Notifications"));
export const ModifyCommunicationSettingsPage = lazyWithRetry(() => import("../Pages/Profile/Settings/CommunicationSetting/ModifyCommunicationSettingsPage"));
export const ExternalNotificationPreferencesPage = lazyWithRetry(() => import("../Pages/Profile/Settings/ExternalNotificationPreferences/ExternalNotificationPreferencesPage"));
export const SettingsHub = lazyWithRetry(() => import("../Pages/Settings/SettingsHub"));
export const SettingsProfilePage = lazyWithRetry(() => import("../Pages/Settings/SettingsProfilePage"));
export const SettingsCommunicationPage = lazyWithRetry(() => import("../Pages/Settings/SettingsCommunicationPage"));
export const SettingsSecurityPage = lazyWithRetry(() => import("../Pages/Settings/SettingsSecurityPage"));
export const SettingsAiConnectionPage = lazyWithRetry(() => import("../Pages/Settings/SettingsAiConnectionPage"));
export const SettingsSavedFiltersPage = lazyWithRetry(() => import("../Pages/Settings/SettingsSavedFiltersPage"));
export const SettingsVersionPage = lazyWithRetry(() => import("../Pages/Settings/SettingsVersionPage"));
export const SettingsConfidentialSharePage = lazyWithRetry(() => import("../Pages/Settings/SettingsConfidentialSharePage"));
export const SettingsDynamicTranslationsPage = lazyWithRetry(() => import("../Pages/Settings/SettingsDynamicTranslationsPage"));
export const SettingsPlatformPage = lazyWithRetry(() => import("../Pages/Settings/SettingsPlatformPage"));
export const ConfidentialShareViewPage = lazyWithRetry(() => import("../Pages/Share/ConfidentialShareViewPage"));
export const MetricView = lazyWithRetry(() => import("../Pages/System/MetricView/MetricView"));
export const MessageEvent = lazyWithRetry(() => import("../Pages/Management/MessageEvent/MessageEvent"));
export const Recipient = lazyWithRetry(() => import("../Pages/Management/Recipient/Recipient"));
export const Cache = lazyWithRetry(() => import("../Pages/System/Cache/cache"));
export const DashlyEmail = lazyWithRetry(() => import("../Pages/System/DashlyEmail/DashlyEmail"));
export const GenericSelectItem = lazyWithRetry(() => import("../Pages/System/GenericSelectItem/GenericSelectItem"));
export const ExternalUser = lazyWithRetry(() => import("../Pages/System/ExternalUser/ExternalUser"));
export const Alerts = lazyWithRetry(() => import("../Pages/Alert/Alerts"));
export const AlertDetail = lazyWithRetry(() => import("../Pages/Alert/AlertDetail"));
export const Processflows = lazyWithRetry(() => import("../Pages/Processflow/Processflows"));
export const ProcessflowDetail = lazyWithRetry(() => import("../Pages/Processflow/ProcessflowDetail"));
export const Objectives = lazyWithRetry(() => import("../Pages/Objective/Objectives"));
export const ObjectiveDetail = lazyWithRetry(() => import("../Pages/Objective/ObjectiveDetail"));
export const Projects = lazyWithRetry(() => import("../Pages/Project/Projects"));
export const ProjectDetail = lazyWithRetry(() => import("../Pages/Project/ProjectDetail"));
export const DataHero = lazyWithRetry(() => import("../Pages/DataHero"));
export const DashboardBuilder = lazyWithRetry(() => import("../Pages/Management/DashboardBuilder/DashboardBuilder").then((module) => ({ default: module.DashboardBuilder })));
// Also lazy (revised after measuring the eager graph): the OAuth callback page drags the
// datasource/OAuth services and marketplace UI into the shell, and the project catalog
// provider drags ProjectService. Both are only needed on their own routes.
export const MarketplaceOAuthCallbackPage = lazyWithRetry(() => import("../Pages/Marketplace/MarketplaceOAuthCallbackPage"));
export const ProjectCatalogProvider = lazyWithRetry(() => import("../Pages/Project/ProjectCatalogContext").then((module) => ({ default: module.ProjectCatalogProvider })));
// The error page is lazy too (the ErrorBoundary loads it on demand as well): its generic
// form field pulled ~25 input components into the shell.
export const ErrorHandling = lazyWithRetry(() => import("../Pages/ErrorHandling/ErrorHandling"));

// ---------------------------------------------------------------------------
// Prefetch registry: which chunks to warm for a navigation target. Exact match
// wins, then the longest prefix ("/system/metric" before "/system/metric-view"
// is avoided by the exact match; "/dashboard/<uuid>" hits the "/dashboard/" prefix).
// ---------------------------------------------------------------------------
type Preloadable = { preload: () => Promise<void> };

const ROUTE_PRELOADS: Array<[string, Preloadable[]]> = [
	["/", [HomeScreen]],
	["/dashboards", [DashboardViewer]],
	["/dashboard/", [DashboardView]],
	[MARKETPLACE_ROUTE, [Marketplace]],
	["/ask-ai", [AskAI]],
	[ALERT_LIST_PATH, [Alerts]],
	[PROJECT_LIST_PATH, [ProjectCatalogProvider, Projects]],
	["/processflows", [Processflows]],
	[OBJECTIVE_LIST_PATH, [Objectives]],
	["/settings", [SettingsHub]],
	["/settings/saved-filters", [SettingsSavedFiltersPage]],
	["/notifications", [Notifications]],
	["/data-hero", [DataHero]],
	["/management/access", [AccessCenter]],
	["/management/dashboard-category", [DashboardCategory]],
	["/system/metric", [Metric]],
	["/system/metric-view", [MetricView]],
	["/system/cache", [Cache]],
	["/system/dashly-email", [DashlyEmail]],
	["/system/generic-select-item", [GenericSelectItem]],
	["/system/external-user", [ExternalUser]],
	["/system/dataservice", [Dataservice]],
];

const normalizePath = (path: string): string => {
	const withoutQuery = (path || "/").split("?")[0].split("#").pop() || "/";
	const trimmed = withoutQuery.replace(/\/+$/, "");
	return trimmed === "" ? "/" : trimmed;
};

/**
 * Kill switch for route prefetching (sign-in landing page + sidebar hover/focus).
 * Set to `false` after prefetching caused errors in production (2026-09-03); the
 * lazy loading itself is unaffected — routes are simply fetched on navigation again.
 * Flip back to `true` once the root cause is fixed.
 */
export const ROUTE_PREFETCH_ENABLED = true;

/** At most this many route chunks are warmed at the same time (no request bursts). */
const PREFETCH_MAX_CONCURRENT = 2;
/** After a failed prefetch, stop prefetching for a while: retrying against a failing server only makes it worse. */
const PREFETCH_PAUSE_AFTER_FAILURE_MS = 60_000;

let prefetchActive = 0;
let prefetchPausedUntil = 0;
const prefetchQueue: Array<() => Promise<void>> = [];
const prefetchedPaths = new Set<string>();

const runPrefetchQueue = (): void => {
	while (prefetchActive < PREFETCH_MAX_CONCURRENT && prefetchQueue.length > 0) {
		const job = prefetchQueue.shift();
		if (!job) {
			return;
		}
		prefetchActive++;
		void job().finally(() => {
			prefetchActive--;
			runPrefetchQueue();
		});
	}
};

/**
 * Warms the chunk(s) behind a route path. Best effort and never throws: a failed
 * prefetch is dropped (and prefetching pauses for a while), the real load at
 * navigation time gets its own attempt. Each path is prefetched at most once per
 * page load; loads are queued with a small concurrency cap.
 */
export const preloadRouteForPath = (path: string): Promise<void> => {
	if (!ROUTE_PREFETCH_ENABLED || Date.now() < prefetchPausedUntil) {
		return Promise.resolve();
	}
	const target = normalizePath(path);
	if (prefetchedPaths.has(target)) {
		return Promise.resolve();
	}
	let match = ROUTE_PRELOADS.find(([route]) => normalizePath(route) === target);
	if (!match) {
		match = ROUTE_PRELOADS
			.filter(([route]) => route !== "/" && target.startsWith(normalizePath(route)))
			.sort((a, b) => b[0].length - a[0].length)[0];
	}
	if (!match) {
		return Promise.resolve();
	}
	prefetchedPaths.add(target);
	const components = match[1];
	return new Promise<void>((resolve) => {
		prefetchQueue.push(() =>
			Promise.all(components.map((component) => component.preload()))
				.then(() => undefined)
				.catch(() => {
					prefetchedPaths.delete(target); // may be retried after the pause
					prefetchPausedUntil = Date.now() + PREFETCH_PAUSE_AFTER_FAILURE_MS;
				})
				.finally(resolve),
		);
		runPrefetchQueue();
	});
};

export type { LazyRouteComponent };
